import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Button component that matches Hooray Power's branding
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  type = 'button',
  to = null,
  href = null,
  onClick,
  ...props
}) => {
  // Base button styles
  const baseStyles = 'font-mont font-medium rounded-full transition-all duration-300 inline-flex items-center justify-center';
  
  // Size variations
  const sizeStyles = {
    sm: 'px-4 py-1.5 text-sm',
    md: 'px-6 py-2.5 text-base',
    lg: 'px-8 py-3 text-lg',
  };
  
  // Variant styles
  const variantStyles = {
    primary: `bg-hooray-red text-white hover:bg-hooray-yellow hover:text-hooray-red focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-hooray-yellow ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
    secondary: `bg-white border border-hooray-red text-hooray-red hover:bg-hooray-red hover:text-white focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-hooray-red ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
    outline: `border border-gray-300 text-hooray-dark-text hover:border-hooray-red hover:text-hooray-red focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-hooray-red ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
    text: `text-hooray-red underline hover:text-hooray-yellow focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-hooray-yellow ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
    cta: `bg-hooray-red text-white hover:bg-hooray-yellow hover:text-hooray-red focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-hooray-yellow ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
    ctaSecondary: `bg-hooray-beige-40 text-hooray-red hover:bg-hooray-yellow hover:text-hooray-red focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-hooray-yellow ${
      disabled ? 'opacity-60 cursor-not-allowed' : ''
    }`,
  };
  
  // Combine all styles
  const buttonStyles = `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`;
  
  // Button content based on variant
  const buttonContent = (
    <span>{children}</span>
  );
  
  // If it's a link (internal route)
  if (to) {
    return (
      <Link
        to={to}
        className={buttonStyles}
        {...props}
      >
        {buttonContent}
      </Link>
    );
  }
  
  // If it's an external link
  if (href) {
    return (
      <a
        href={href}
        className={buttonStyles}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {buttonContent}
      </a>
    );
  }
  
  // Default button
  return (
    <button
      type={type}
      className={buttonStyles}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {buttonContent}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'text', 'cta', 'ctaSecondary']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  to: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
};

export default Button; 